'use client';

import { useRouter, useParams } from 'next/navigation';
import { useYmmFormContext } from '../../context/ymm-form-context';
import { SelectField } from '@/vibes/soul/form/select-field';

import { X, FilterIcon, Loader2 } from 'lucide-react';

import { Button } from '@/vibes/soul/primitives/button';
import clsx from 'clsx';

import { Popover, PopoverTrigger, PopoverPortal, PopoverContent, PopoverClose, PopoverArrow } from '@radix-ui/react-popover';

/** Strip tag prefix (yr_, mk_, md_) from field value for URL param. */
function toSearchParamValue(value: string, prefix: string): string {
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

export interface TopNavFilterProps {
  className?: string;
}

export function TopNavFilter(props: TopNavFilterProps) {
  const router = useRouter();
  const params = useParams();
  const locale = params?.locale as string | undefined;
  const pathname = locale ? `/${locale}/fitments` : '/fitments';
  const {
    isLoading,
    formData,
    fieldState,
    setYear,
    setMake,
    setModel,
    resetSelections,
    persistYmmSelection,
    clearPreserveFromRestore,
    appDisabled
  } = useYmmFormContext();

  const { year, make, model } = formData;

  const onYearChange = (value: string) => {
    setYear(value);
  };

  const onMakeChange = (value: string) => {
    setMake(value);
  };

  const onModelChange = (value: string) => {
    setModel(value);
  };

  const onReset = () => {
    resetSelections();
  };

  const onSearch = () => {
    const yr = fieldState.year.current_val ? toSearchParamValue(fieldState.year.current_val, 'yr_') : '';
    const mk = fieldState.make.current_val ? toSearchParamValue(fieldState.make.current_val, 'mk_') : '';
    const md = fieldState.model.current_val ? toSearchParamValue(fieldState.model.current_val, 'md_') : '';

    if (yr && mk && md) {
      persistYmmSelection();
      clearPreserveFromRestore();
      router.push(`${pathname}?yr=${yr}&mk=${mk}&md=${md}`);
    }
  };

  if (appDisabled) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="primary" shape="square" size="medium" type="button" className="uppercase min-w-full @4xl:min-w-10">
          <span className="@4xl:hidden @6xl:inline">Shop by Vehicle</span>
          <FilterIcon size={14} strokeWidth={3} />
        </Button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent className="bg-primary-black w-screen md:w-80 relative" sideOffset={5} align="start">
          <section className={clsx(' @container max-w-2xl mx-auto', props.className)}>
            <form
              className="grid items-center gap-6 px-6 pb-6 pt-12"
              onSubmit={(e) => {
                e.preventDefault();
                onSearch();
              }}
            >
              <div className={clsx(`relative @3xl:col-span-4 @5xl:col-span-2`,
                isLoading.year && 'pointer-events-none',
                isLoading.year && 'hover:after:translate-x-0')
              }>
                <SelectField
                  key={`ymm-year-${fieldState.year.current_val}-${(year?.options?.length ?? 0)}`}
                  hideLabel={true}
                  label="Year"
                  className="bg-white"
                  name="year"
                  value={fieldState.year.current_val}
                  placeholder={year.placeholder || 'Year'}
                  options={year?.options || []}
                  onValueChange={onYearChange}
                  colorScheme="light"
                />
                {isLoading.year && (
                  <span
                    className={clsx(
                      'absolute inset-0 grid place-content-center transition-all duration-300 ease-in-out translate-y-0 opacity-100',
                    )}
                  >
                    <Loader2 className="animate-spin text-[var(--button-loader-icon,hsl(var(--foreground)))]" />
                  </span>
                )}
              </div>
              <div className={clsx(`relative @3xl:col-span-4 @5xl:col-span-2`,
                isLoading.make && 'pointer-events-none',
                isLoading.make && 'hover:after:translate-x-0')
              }>
                <SelectField
                  key={`ymm-make-${fieldState.make.current_val}-${(make.options?.length ?? 0)}`}
                  hideLabel={true}
                  label="Make"
                  className="bg-white"
                  name="make"
                  value={fieldState.make.current_val}
                  placeholder={make.placeholder || 'Make'}
                  options={make.options || []}
                  onValueChange={onMakeChange}
                  colorScheme="light"
                  disabled={make.options?.length === 0}
                />
                {isLoading.make && (
                  <span
                    className={clsx(
                      'absolute inset-0 grid place-content-center transition-all duration-300 ease-in-out translate-y-0 opacity-100',
                    )}
                  >
                    <Loader2 className="animate-spin text-[var(--button-loader-icon,hsl(var(--foreground)))]" />
                  </span>
                )}
              </div>
              <div className={clsx(`relative @3xl:col-span-4 @5xl:col-span-2`,
                isLoading.model && 'pointer-events-none')
              }>
                <SelectField
                  key={`ymm-model-${fieldState.model.current_val}-${(model.options?.length ?? 0)}`}
                  hideLabel={true}
                  label="Model"
                  className="bg-white @3xl:col-span-4 @5xl:col-span-2"
                  name="model"
                  value={fieldState.model.current_val}
                  placeholder={model.placeholder || 'Model'}
                  options={model.options || []}
                  onValueChange={onModelChange}
                  colorScheme="light"
                  disabled={model.options?.length === 0}
                />
                {isLoading.model && (
                  <span
                    className={clsx(
                      'absolute inset-0 grid place-content-center transition-all duration-300 ease-in-out translate-y-0 opacity-100',
                    )}
                  >
                    <Loader2 className="animate-spin text-[var(--button-loader-icon,hsl(var(--foreground)))]" />
                  </span>
                )}
              </div>
              <div className="flex @3xl:col-span-4 @5xl:col-span-2 grid grid-cols-2 gap-4 @7xl:gap-8">
                <Button variant="primary" shape="square" size="small" type="button" onClick={onSearch}>
                  Search
                </Button>
                <Button variant="secondary"
                  shape="square"
                  size="small"
                  className="text-white bg-transparent border-white"
                  onClick={onReset}
                >Reset</Button>
              </div>
            </form>
          </section>
          <PopoverClose className="PopoverClose" aria-label="Close">
            <X size={20} strokeWidth={3} className="text-white absolute top-4 right-4" />
          </PopoverClose>
          <PopoverArrow className="PopoverArrow" />
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}